import React, { useState, useMemo } from "react";
import { AppLayout } from "@/components/layout/AppLayout";
import { useTranslation } from "@/lib/i18n";
import { useTools, useToolsViewMode, useToggleToolViewMode } from "@/lib/tools-store";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Search, LayoutGrid, List, SlidersHorizontal, Download } from "lucide-react";
import { ToolCard } from "@/components/tools/ToolCard";
import { ToolTable } from "@/components/tools/ToolTable";
import { ToolModal } from "@/components/tools/ToolModal";
import { motion, AnimatePresence } from "framer-motion";
import type { Tool } from "@/lib/tools-store";
export default function ToolsPage() {
  const { t } = useTranslation();
  const tools = useTools();
  const viewMode = useToolsViewMode();
  const toggleViewMode = useToggleToolViewMode();
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTool, setEditingTool] = useState<Tool | undefined>(undefined);
  const filteredTools = useMemo(() => {
    return tools.filter((tool) => {
      const matchesSearch = tool.name.toLowerCase().includes(search.toLowerCase()) ||
                            tool.description.toLowerCase().includes(search.toLowerCase());
      const matchesCategory = categoryFilter === "all" || tool.category === categoryFilter;
      return matchesSearch && matchesCategory;
    });
  }, [tools, search, categoryFilter]);
  const categories = ['CRM', 'Finance', 'Communication', 'Productivity', 'Sales', 'Development', 'Security'];
  const openCreateModal = () => {
    setEditingTool(undefined);
    setIsModalOpen(true);
  };
  const openEditModal = (tool: Tool) => {
    setEditingTool(tool);
    setIsModalOpen(true);
  };
  return (
    <AppLayout container>
      <div className="space-y-8">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-foreground">{t('tools_title')}</h1>
            <p className="text-muted-foreground mt-1">Connect your enterprise ecosystem with secure AI capabilities.</p>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" className="gap-2 h-11">
              <Download className="w-4 h-4" /> {t('import_openapi')}
            </Button>
            <Button
              className="gap-2 h-11 px-6 bg-gradient-primary text-primary-foreground shadow-glow hover:opacity-95"
              onClick={openCreateModal}
            >
              <Plus className="w-5 h-5" />
              {t('create_tool')}
            </Button>
          </div>
        </div>
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between bg-card/50 p-4 rounded-xl border border-white/5 backdrop-blur-sm">
          <div className="relative w-full md:max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder={t('search_tools')}
              className="pl-10 bg-background/50"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="flex items-center gap-3 w-full md:w-auto">
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-full md:w-[180px] bg-background/50">
                <SlidersHorizontal className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex border rounded-lg overflow-hidden shrink-0">
              <Button
                variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
                size="icon"
                onClick={toggleViewMode}
                className="rounded-none h-10 w-10"
              >
                <LayoutGrid className="w-4 h-4" />
              </Button>
              <Button
                variant={viewMode === 'table' ? 'secondary' : 'ghost'}
                size="icon"
                onClick={toggleViewMode}
                className="rounded-none h-10 w-10"
              >
                <List className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
        <div className="min-h-[400px]">
          <AnimatePresence mode="wait">
            {viewMode === 'grid' ? (
              <motion.div
                key="grid"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
              >
                {filteredTools.map((tool) => (
                  <ToolCard key={tool.id} tool={tool} onEdit={openEditModal} />
                ))}
              </motion.div>
            ) : (
              <motion.div key="table" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <ToolTable tools={filteredTools} onEdit={openEditModal} />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        <ToolModal open={isModalOpen} onOpenChange={setIsModalOpen} editTool={editingTool} />
      </div>
    </AppLayout>
  );
}
